import { type FC, useEffect, useRef, useState } from 'react';
import { Alert, Box, CircularProgress, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch } from 'react-redux';
import {
  markAlertAsCompleted,
  markAlertAsRead,
  updateAlertElapsedTime,
} from 'src/redux/slices/Compliance/CertificationAlertsSlice';
import type {
  CertificationAlert as CertificationAlertType,
  AlertContext,
} from 'src/redux/slices/Compliance/CertificationAlertsSlice';

interface CertificationAlertProps {
  alert: CertificationAlertType;
  context: AlertContext;
  isUserActive: boolean;
  isPaused: boolean;
  hideCloseButton?: boolean;
  disableAutoRead?: boolean;
  isVisible?: boolean;
}

const CertificationAlert: FC<CertificationAlertProps> = ({
  alert,
  context,
  isUserActive,
  isPaused,
  hideCloseButton = false,
  disableAutoRead = false,
  isVisible = true,
}) => {
  const remaining = alert.timeout - (alert.totalElapsedTime ?? 0);

  const dispatch = useDispatch();
  const [progress, setProgress] = useState(
    Math.max(0, (remaining / alert.timeout) * 100)
  );

  const [isHovered, setIsHovered] = useState(false);
  const elapsedRef = useRef(alert.totalElapsedTime || 0);

  useEffect(() => {
    if (!disableAutoRead && isUserActive && !alert.read) {
      dispatch(markAlertAsRead({ context, id: alert.id }));
    }
  }, [disableAutoRead, isUserActive, alert.read, alert.id, context, dispatch]);

  useEffect(() => {
    if (elapsedRef.current < alert.totalElapsedTime) {
      elapsedRef.current = alert.totalElapsedTime;
      setProgress(
        Math.max(
          0,
          ((alert.timeout - alert.totalElapsedTime) / alert.timeout) * 100
        )
      );
    }
  }, [alert.timeout, alert.totalElapsedTime]);

  useEffect(() => {
    return () => {
      if (
        elapsedRef.current !== null &&
        elapsedRef.current > alert.totalElapsedTime
      ) {
        dispatch(
          updateAlertElapsedTime({
            context,
            id: alert.id,
            elapsedTime: elapsedRef.current,
          })
        );
      }
    };
  }, [alert.id, alert.totalElapsedTime, context, dispatch]);

  useEffect(() => {
    // Only run timer when alert is visible
    if (!isVisible) {
      return;
    }

    const remainingTime = alert.timeout - elapsedRef.current;

    if (remainingTime <= 0) {
      // Mark as read when alert expires naturally
      dispatch(markAlertAsRead({ context, id: alert.id }));
      dispatch(markAlertAsCompleted({ context, id: alert.id }));
      return;
    }

    let progressInterval: ReturnType<typeof setInterval> | null = null;

    const startTimer = (): void => {
      const intervalTimeInMS = 100;

      progressInterval = setInterval(() => {
        if (isPaused || isHovered) {
          return;
        }
        const remaining =
          alert.timeout - (elapsedRef.current + intervalTimeInMS);
        const newProgress = Math.max(0, (remaining / alert.timeout) * 100);
        setProgress(newProgress);
        elapsedRef.current += intervalTimeInMS;

        if (newProgress <= 0 && progressInterval) {
          clearInterval(progressInterval);
          // Mark as read when alert expires naturally
          dispatch(markAlertAsRead({ context, id: alert.id }));
          dispatch(markAlertAsCompleted({ context, id: alert.id }));
        }
      }, intervalTimeInMS);
    };

    if (!isPaused && !isHovered) {
      startTimer();
    }

    return () => {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      dispatch(
        updateAlertElapsedTime({
          context,
          id: alert.id,
          elapsedTime: elapsedRef.current,
        })
      );
    };
  }, [
    alert.id,
    alert.timeout,
    context,
    dispatch,
    isPaused,
    isHovered,
    isVisible,
  ]);

  const handleClose = (): void => {
    if (isUserActive) {
      dispatch(markAlertAsRead({ context, id: alert.id }));
    }
    dispatch(markAlertAsCompleted({ context, id: alert.id }));
  };

  return (
    <Alert
      sx={{
        flex: '1 0 auto',
        alignItems: 'center',
      }}
      severity={alert.severity}
      icon={alert.icon}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      action={
        hideCloseButton ? undefined : (
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress
              variant="determinate"
              value={progress}
              size={32}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                color: 'action.disabled',
                transform: 'scaleX(-1) rotate(-90deg) !important',
              }}
            />
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={handleClose}
              sx={{
                width: 32,
                height: 32,
              }}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          </Box>
        )
      }
    >
      {alert.message}
    </Alert>
  );
};

export default CertificationAlert;
